import { useEffect, useState } from 'react';
import { X, Package, MapPin, Truck } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import PhaseTimeline from './PhaseTimeline';
import EventLog from './EventLog';
import NotesEditor from './NotesEditor';

type FullOrder = {
  seller_order_id: string;
  buyer_order_id: string | null;
  marketplace: string;
  stage: string;
  is_delayed: boolean;
  product_name: string | null;
  asin: string | null;
  sku: string | null;
  quantity: number;
  price_cad: number | null;
  depot_code: string | null;
  depot_id: string | null;
  notes: string | null;
  tags: string[];
  order_date: string | null;
  deadline_date: string | null;
  order_placed_at: string | null;
  purchased_at: string | null;
  arrived_warehouse_at: string | null;
  tracking_assigned_at: string | null;
  delivered_at: string | null;
};

type Shipment = {
  carrier_name: string | null;
  tracking_number: string | null;
  tracking_url: string | null;
  carrier_status: string | null;
};

/** Sağdan kayan sipariş detay paneli. ESC veya backdrop'a tıklayınca kapanır. */
export default function OrderDrawer({
  sellerOrderId,
  open,
  onClose,
}: {
  sellerOrderId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  const [order, setOrder] = useState<FullOrder | null>(null);
  const [shipment, setShipment] = useState<Shipment | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    if (open) {
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }
  }, [open, onClose]);

  useEffect(() => {
    if (!sellerOrderId || !open) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      const [orderRes, shipRes] = await Promise.all([
        supabase.from('orders').select('*').eq('seller_order_id', sellerOrderId).single(),
        supabase
          .from('shipments')
          .select('carrier_name, tracking_number, tracking_url, carrier_status')
          .eq('seller_order_id', sellerOrderId)
          .order('updated_at', { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);
      if (cancelled) return;
      setOrder((orderRes.data as FullOrder) ?? null);
      setShipment((shipRes.data as Shipment) ?? null);
      setLoading(false);
    }
    load();
    return () => { cancelled = true; };
  }, [sellerOrderId, open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* backdrop */}
      <div className="flex-1 bg-black/40" onClick={onClose} aria-hidden="true" />

      {/* drawer */}
      <aside className="w-full sm:w-[480px] lg:w-[560px] bg-white shadow-xl overflow-y-auto">
        <header className="sticky top-0 bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Package className="w-4 h-4 text-slate-600 flex-shrink-0" />
            <h2 className="text-sm font-semibold text-slate-900 truncate">Sipariş Detayı</h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900">
            <X className="w-4 h-4" />
          </button>
        </header>

        {loading || !order ? (
          <div className="p-6 text-sm text-slate-500">Yükleniyor…</div>
        ) : (
          <div className="p-4 space-y-5">
            {/* ID kartı */}
            <section>
              <div className="text-[10px] uppercase text-slate-400 mb-1">Sipariş Kimliği</div>
              <div className="font-mono text-sm text-slate-900">{order.seller_order_id}</div>
              <div className="font-mono text-xs text-slate-500 mt-0.5">
                {order.buyer_order_id ?? <span className="italic text-slate-300">buyer ID atanmamış</span>}
              </div>
            </section>

            {/* Ürün */}
            <section>
              <div className="text-[10px] uppercase text-slate-400 mb-1">Ürün</div>
              <div className="text-sm text-slate-900">{order.product_name ?? '—'}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                ASIN: <span className="font-mono">{order.asin ?? '—'}</span>
                {order.sku && <> · SKU: <span className="font-mono">{order.sku}</span></>}
                {order.price_cad && <> · {order.price_cad.toFixed(2)} CAD</>}
              </div>
            </section>

            {/* Depo + kargo */}
            <section className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] uppercase text-slate-400 mb-1 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Depo
                </div>
                <div className="text-sm text-slate-900">
                  {order.depot_code ?? '—'}
                  {order.depot_id && <span className="text-xs text-slate-500 ml-1">({order.depot_id})</span>}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase text-slate-400 mb-1 flex items-center gap-1">
                  <Truck className="w-3 h-3" /> Kargo
                </div>
                <div className="text-sm text-slate-900">
                  {shipment?.tracking_number ? (
                    <>
                      <div className="font-mono text-xs">{shipment.tracking_number}</div>
                      <div className="text-[10px] text-slate-500">{shipment.carrier_name ?? '—'}</div>
                      {shipment.tracking_url && (
                        <a href={shipment.tracking_url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-indigo-600 hover:underline">
                          takip linki ↗
                        </a>
                      )}
                    </>
                  ) : (
                    <span className="text-slate-400">çıkış yapmadı</span>
                  )}
                </div>
              </div>
            </section>

            {/* Faz timeline */}
            <section>
              <div className="text-[10px] uppercase text-slate-400 mb-2">Faz Akışı</div>
              <PhaseTimeline ts={order} />
            </section>

            {/* Not + etiket */}
            <section>
              <div className="text-[10px] uppercase text-slate-400 mb-2">Notlar & Etiketler</div>
              <NotesEditor
                sellerOrderId={order.seller_order_id}
                initialNotes={order.notes}
                initialTags={order.tags ?? []}
              />
            </section>

            {/* Event log */}
            <section>
              <div className="text-[10px] uppercase text-slate-400 mb-2">Event Geçmişi</div>
              <EventLog sellerOrderId={order.seller_order_id} />
            </section>
          </div>
        )}
      </aside>
    </div>
  );
}
